import { useState, useEffect, useRef } from 'react';

interface LoadingState {
  /** 合併後的載入狀態，當任一註冊的載入狀態為 true 時，此值為 true */
  isLoading: boolean;

  /**
   * 設置指定ID的載入狀態
   * @param id 載入狀態的唯一識別符
   */
  setLoading: (id: string, isLoading: boolean) => void;

  /** 重置所有載入狀態 */
  resetAllLoading: () => void;
}

interface LoadingStateOptions {
  /**
   * 載入狀態關閉的延遲時間（毫秒）
   * 預設: 300ms
   */
  delayMs?: number;

  /** 載入狀態的初始值 */
  initialState?: Record<string, boolean>;

  /**
   * 載入狀態變化的回調函數
   * @param isLoading 當前合併載入狀態
   */
  onLoadingChange?: (isLoading: boolean, loadingStates: Record<string, boolean>) => void;
}

/**
 * 通用載入狀態管理 Hook
 * 可以管理多個載入狀態，確保無縫過渡，避免介面閃爍
 * @returns LoadingState 物件
 */
export const useLoadingState = (options: LoadingStateOptions = {}): LoadingState => {
  const { delayMs = 300, initialState = {}, onLoadingChange } = options;

  // 追蹤載入狀態
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(initialState);

  // 合併後的載入狀態
  const [isLoading, setIsLoading] = useState<boolean>(
    Object.values(initialState).some((state) => state === true)
  );

  // 延遲關閉計時器
  const loadingTimer = useRef<NodeJS.Timeout | null>(null);

  // 更新合併狀態
  useEffect(() => {
    const anyLoading = Object.values(loadingStates).some((state) => state === true);

    if (anyLoading) {
      // 有載入狀態為 true 時，立即顯示載入
      if (loadingTimer.current) {
        clearTimeout(loadingTimer.current);
        loadingTimer.current = null;
      }

      if (!isLoading) {
        setIsLoading(true);
        onLoadingChange?.(true, { ...loadingStates });
      }
    } else {
      // 全部完成時，延遲關閉載入
      if (isLoading && !loadingTimer.current) {
        loadingTimer.current = setTimeout(() => {
          setIsLoading(false);
          loadingTimer.current = null;
          onLoadingChange?.(false, { ...loadingStates });
        }, delayMs);
      }
    }

    // 清理
    return () => {
      if (loadingTimer.current) {
        clearTimeout(loadingTimer.current);
      }
    };
  }, [loadingStates, isLoading, delayMs, onLoadingChange]);

  // 設置特定ID的載入狀態
  const setLoading = (id: string, isLoading: boolean) => {
    setLoadingStates((prev) => {
      if (prev[id] === isLoading) return prev;
      return { ...prev, [id]: isLoading };
    });
  };

  // 重置所有載入狀態
  const resetAllLoading = () => {
    setLoadingStates({});

    // 清除計時器
    if (loadingTimer.current) {
      clearTimeout(loadingTimer.current);
      loadingTimer.current = null;
    }

    setIsLoading(false);
    onLoadingChange?.(false, {});
  };

  return {
    isLoading,
    setLoading,
    resetAllLoading,
  };
};
